import { useState, useEffect } from 'react';
import { IndianRupee, Loader2, Check } from 'lucide-react';
import api from '../lib/api';
import useAsync from '../hooks/useAsync';
import Loader, { ErrorState } from '../components/Loader';

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      disabled={disabled}
      className={`relative h-7 w-12 rounded-pill transition ${on ? 'bg-brand' : 'bg-white/15'} ${disabled ? 'opacity-60' : ''}`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${on ? 'left-6' : 'left-1'}`}
      />
    </button>
  );
}

export default function AdminSettings() {
  const { data, loading, error, reload } = useAsync(
    () => api.get('/settings').then((r) => r.data),
    []
  );
  const [showPricing, setShowPricing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setShowPricing(data.showPricing !== false);
  }, [data]);

  const save = async (next) => {
    setShowPricing(next);
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/settings', { showPricing: next });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setShowPricing(!next); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader label="Loading settings…" />;
  if (error) return <ErrorState message="Couldn't load settings." onRetry={reload} />;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Settings</h1>
        <p className="text-sm text-ink-400">Control how content appears on the website & app.</p>
      </div>

      <div className="glass flex items-center justify-between gap-4 p-6">
        <div className="flex items-start gap-4">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-gradient text-white">
            <IndianRupee size={20} />
          </span>
          <div>
            <p className="font-display font-semibold text-white">Show package pricing</p>
            <p className="mt-1 max-w-md text-sm text-ink-400">
              When off, customers see “Price on request” everywhere (packages, add-ons,
              customize summary & WhatsApp message). Admin always sees real prices.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saving && <Loader2 size={16} className="animate-spin text-ink-400" />}
          {saved && <span className="flex items-center gap-1 text-xs text-success"><Check size={14} /> Saved</span>}
          <Toggle on={showPricing} onChange={save} disabled={saving} />
        </div>
      </div>

      <p className="text-xs text-ink-500">
        Changes apply instantly on next page load for visitors (web & app).
      </p>
    </div>
  );
}
